import { type DragEvent, type FormEvent, useEffect, useMemo, useState } from "react";
import { uploadAdminCatalogImage } from "../../lib/admin-catalog";
import { createAdminProject, fetchAdminProject, fetchAdminProjects, updateAdminProject, type AdminProjectItem } from "../../lib/admin-projects";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type EditorState = {
  id: number | null;
  title: string;
  slug: string;
  image: string;
  cost: string;
  materialCost: string;
  summary: string;
  description: string;
};

const emptyEditorState: EditorState = {
  id: null,
  title: "",
  slug: "",
  image: "",
  cost: "0",
  materialCost: "0",
  summary: "",
  description: ""
};

function toEditorState(project: AdminProjectItem): EditorState {
  return {
    id: project.id,
    title: project.title,
    slug: project.slug,
    image: project.image,
    cost: String(project.cost),
    materialCost: String(project.materialCost),
    summary: project.summary,
    description: project.description
  };
}

export function AdminProjectsPage() {
  const [projects, setProjects] = useState<AdminProjectItem[]>([]);
  const [selectedProject, setSelectedProject] = useState<AdminProjectItem | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalProjects: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalProjects: 0,
    withImageProjects: 0,
    totalProjectValue: 0,
    totalMaterialCost: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const metrics = useMemo(
    () => [
      { label: "Total projects", value: summary.totalProjects },
      { label: "With image", value: summary.withImageProjects },
      { label: "Project value", value: formatCurrency(summary.totalProjectValue) },
      { label: "Material cost", value: formatCurrency(summary.totalMaterialCost) }
    ],
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchAdminProjects(params);
        if (!ignore) {
          setProjects(result.projects);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load projects.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [page, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchAdminProjects(params);
    setProjects(result.projects);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreate() {
    setEditor(emptyEditorState);
    setSelectedProject(null);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
  }

  async function openEdit(project: AdminProjectItem) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchAdminProject(project.id);
      setSelectedProject(detail);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load project details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.title.trim()) {
      setError("Project title is required.");
      return;
    }

    const cost = Number(editor.cost);
    const materialCost = Number(editor.materialCost);
    if (Number.isNaN(cost) || cost < 0 || Number.isNaN(materialCost) || materialCost < 0) {
      setError("Project costs must be valid non-negative numbers.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        title: editor.title,
        slug: editor.slug,
        image: editor.image,
        cost,
        materialCost,
        summary: editor.summary,
        description: editor.description
      };

      let projectId = editor.id;
      if (editor.id) {
        await updateAdminProject(editor.id, payload);
      } else {
        const created = await createAdminProject(payload);
        projectId = typeof created?.projectId === "number" ? created.projectId : null;
      }

      await reloadCurrentView();
      if (projectId) {
        const refreshed = await fetchAdminProject(projectId);
        setSelectedProject(refreshed);
        setEditor(toEditorState(refreshed));
      }
      setSuccessMessage(editor.id ? "Project changes saved successfully." : "Project created successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save project.");
    } finally {
      setSaving(false);
    }
  }

  async function handleImageUpload(files: FileList | null) {
    if (!files || files.length === 0) {
      return;
    }

    setUploadingImage(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const uploaded = await uploadAdminCatalogImage(files[0]);
      setEditor((current) => ({
        ...current,
        image: uploaded.image.url
      }));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Unable to upload project image.");
    } finally {
      setUploadingImage(false);
    }
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
    void handleImageUpload(event.dataTransfer.files);
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    if (!dragActive) {
      setDragActive(true);
    }
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Projects</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight">Showcase and publishing</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">
            Manage showcase builds, keep project copy polished, and maintain clean visual assets for the public-facing brand.
          </p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
          New project
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 break-words text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(360px,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="admin-card rounded-[28px] p-5">
            <input
              className="field-control w-full px-4 py-3 text-sm"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by project title, summary, or slug"
              value={query}
            />
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="space-y-3">
            {loading ? (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">Loading projects...</div>
            ) : projects.length > 0 ? (
              projects.map((project) => (
                <button
                  className="admin-card block w-full rounded-[28px] p-5 text-left transition hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                  key={project.id}
                  onClick={() => void openEdit(project)}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">#{project.id} · {project.slug || "No slug"}</p>
                      <h2 className="mt-2 text-xl font-semibold tracking-tight text-nv-ink">{project.title}</h2>
                      <p className="mt-3 line-clamp-3 text-sm leading-7 text-nv-slate">{project.summary || "No summary added."}</p>
                    </div>
                    {project.image ? (
                      <img
                        alt={project.title}
                        className="h-20 w-24 shrink-0 rounded-2xl object-cover"
                        src={project.image}
                      />
                    ) : (
                      <div className="field-soft flex h-20 w-24 shrink-0 items-center justify-center rounded-2xl text-xs font-semibold uppercase tracking-[0.16em] text-nv-slate">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-nv-slate">
                    <span>Project cost {formatCurrency(project.cost)}</span>
                    <span>Materials {formatCurrency(project.materialCost)}</span>
                    <span>{formatDateTime(project.addedOn)}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">No projects matched the current view.</div>
            )}
          </div>

          <div className="admin-card flex flex-wrap items-center justify-between gap-3 rounded-[28px] p-5 text-sm text-nv-slate">
            <p>
              Page {pagination.page} of {pagination.totalPages} · {pagination.totalProjects} projects
            </p>
            <div className="flex items-center gap-2">
              <button
                className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                disabled={!pagination.hasPreviousPage || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                type="button"
              >
                Previous
              </button>
              <button
                className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                disabled={!pagination.hasNextPage || loading}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{editorOpen ? "Project editor" : "Select a project"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
                  {editorOpen ? (editor.id ? `Project #${editor.id}` : "New project") : "Editorial workspace"}
                </h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select a project to review and edit its publishing data.
              </div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading project details...
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                {successMessage ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">
                    {successMessage}
                  </div>
                ) : null}

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Project image</label>
                    <p className="text-xs text-nv-slate">Upload directly to Cloudinary like catalog images.</p>
                  </div>
                  <label
                    className={`mt-3 flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-[28px] border border-dashed px-6 py-6 text-center transition ${
                      dragActive
                        ? "border-blue-500 bg-blue-50/80 dark:border-cyan-400 dark:bg-cyan-500/10"
                        : "border-black/10 bg-black/[0.02] hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.05]"
                    }`}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-xl text-nv-slate dark:border-white/10">
                      +
                    </div>
                    <p className="mt-4 text-sm font-semibold text-nv-ink">
                      {uploadingImage ? "Uploading project image..." : editor.image ? "Replace project image" : "Drag and drop a project image here"}
                    </p>
                    <p className="mt-2 text-sm text-nv-slate">or click to choose an image file</p>
                    <p className="mt-1 text-xs text-nv-slate">PNG, JPG, WEBP and other image formats are supported.</p>
                    <input
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingImage}
                      onChange={(event) => {
                        void handleImageUpload(event.target.files);
                        event.target.value = "";
                      }}
                      type="file"
                    />
                  </label>
                  {editor.image ? (
                    <div className="mt-4 rounded-[28px] border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
                      <img alt={editor.title || "Project preview"} className="h-52 w-full rounded-[20px] object-cover" src={editor.image} />
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="min-w-0 truncate text-xs text-nv-slate">{editor.image}</p>
                        <button
                          className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300"
                          onClick={() => setEditor((current) => ({ ...current, image: "" }))}
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-3xl border border-dashed border-black/10 p-4 text-sm text-nv-slate dark:border-white/10">
                      No project image yet. Upload one to build the project card.
                    </div>
                  )}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Title</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, title: event.target.value }))} value={editor.title} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Slug</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, slug: event.target.value }))} placeholder="Leave blank to derive from title" value={editor.slug} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Project cost</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setEditor((current) => ({ ...current, cost: event.target.value }))} step="0.01" type="number" value={editor.cost} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Material cost</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setEditor((current) => ({ ...current, materialCost: event.target.value }))} step="0.01" type="number" value={editor.materialCost} />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Summary</label>
                  <textarea className="field-control min-h-[120px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, summary: event.target.value }))} placeholder="Short project summary for cards and previews" value={editor.summary} />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Description</label>
                  <textarea className="field-control min-h-[260px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, description: event.target.value }))} placeholder="Full project description or HTML content" value={editor.description} />
                </div>

                {selectedProject ? (
                  <div className="field-soft rounded-[24px] p-4 text-sm text-nv-slate">
                    Added {formatDateTime(selectedProject.addedOn)} · Public slug {selectedProject.slug || "Not set"}
                  </div>
                ) : null}

                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : editor.id ? "Save changes" : "Create project"}
                  </button>
                  <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                    Close
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
